import {
  NgModule,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  isDevMode,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  provideTranslateHttpLoader,
  TranslateHttpLoader,
} from '@ngx-translate/http-loader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './interceptors/auth.interceptor';

import { byDatePipeModule } from './pipes/by-date-pipe.module';
import { ByOrderNumberModule } from './pipes/by-order-number.module';
import { ByCustomerNameModule } from './pipes/by-customer-name/by-customer-name.module';
import { ByNamePipeModule } from './pipes/by-name.module';
import { SortByModule } from './pipes/sort-by.module';
import { ByMsCarrierModule } from './pipes/by-ms-carrier.module';
import { ByToDateModule } from './pipes/by-to-date/by-to-date.module';
import { ByPriorityCodeModule } from './pipes/by-priority-code.module';
import { ByStatusCodeModule } from './pipes/by-status-code/by-status-code.module';

import { NgbModule, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import '@progress/kendo-angular-intl/locales/it/all';
// 🚨 v2.14.1 T2b (2026-07-09): drop 10 Kendo module dead. Grep audit:
// LayoutModule, ToolBarModule, UploadsModule, FileSelectModule,
// SliderModule, SwitchModule, TextAreaModule, ListViewModule,
// IconsModule, ProgressBarModule → 0 hit selectors nei template
// HTML e 0 DI service usage. ProgressBarModule aveva un residual
// LabelSettings type in dashboard.setPercentLabel field, ma il
// field non è referenziato in template → dead cascade, droppato.
import { LabelModule } from '@progress/kendo-angular-label';
import {
  InputsModule,
  FormFieldModule,
  TextBoxModule,
} from '@progress/kendo-angular-inputs';
import { GridModule } from '@progress/kendo-angular-grid';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { HeaderComponent } from './components/header/header.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { RightSidebarComponent } from './components/right-sidebar/right-sidebar.component';
import { FooterComponent } from './components/footer/footer.component';

import { DashboardComponent } from './components/dashboard/dashboard.component';
import { OrdiniPMComponent } from './components/ordini-pm/ordini-pm.component';
import { LoginComponent } from './components/login/login.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { WarehouseOrdersComponent } from './components/preparazione-ordini/prepare-order/warehouse-orders/warehouse-orders.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';

import { ControlloQualityComponent } from './components/controllo-quality/controllo-quality.component';
import { StatisticheComponent } from './components/statistiche/statistiche.component';
import { SettingsComponent } from './components/settings/settings.component';
import { AssegnaValoreComponent } from './components/settings/assegna-valore/assegna-valore.component';

import { BaseOrdersListComponent } from './components/shared/base-orders-list/base-orders-list.component';
import { BaseOrderscmListComponent } from './components/shared/base-orderscm-list/base-orderscm-list.component';

import { AssegnaOrdineListComponent } from './components/assegna-ordine/assegna-ordine-list/assegna-ordine-list.component';
import { AssegnaOrdineDettagliComponent } from './components/assegna-ordine/assegna-ordine-dettagli/assegna-ordine-dettagli.component';

import { PreparazioneOrdiniComponent } from './components/preparazione-ordini/preparazione-ordini.component';
import { PreparazioneOrdiniDetaggliComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/preparazione-ordini-detaggli.component';
import { CollectionComponent } from './components/preparazione-ordini/collection/collection.component';
import { ConfirmProductsComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-products/confirm-products.component';
import { AddBoxComponent } from './components/shared/order-details/add-box/add-box.component';
import { ConfirmBoxesComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-boxes/confirm-boxes.component';
import { BoxDetailsComponent } from './components/shared/order-details/add-box/box-details/box-details.component';
import { ConfirmDocumentsComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-documents/confirm-documents.component';
import { ConfirmShipmentComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-shipment/confirm-shipment.component';
import { ConfirmCourrierComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-courrier/confirm-courrier.component';

import { OrdiniDaFirmareComponent } from './components/ordini-da-firmare/ordini-da-firmare.component';
import { OrdiniDaFirmareDetaggliComponent } from './components/ordini-da-firmare/ordini-da-firmare-detaggli/ordini-da-firmare-detaggli.component';
import { ConfirmCompleteComponent } from './components/ordini-da-firmare/confirm-complete/confirm-complete.component';

import { StoricoOrdiniComponent } from './components/storico-ordini/storico-ordini.component';
import { StoricoOrdiniDettagliComponent } from './components/storico-ordini/storico-ordini-dettagli/storico-ordini-dettagli.component';

import { BorderòComponent } from './components/borderò/borderò.component';
import { BorderoPageComponent } from './components/ordini-da-firmare/bordero-page/bordero-page.component';
import { BorderòDettagliComponent } from './components/borderò/borderò-dettagli/borderò-dettagli.component';

import { OrdiniParzialiListComponent } from './components/ordini-parziali/ordini-parziali-list/ordini-parziali-list.component';
import { OrdiniParzialiDettagliComponent } from './components/ordini-parziali/ordini-parziali-dettagli/ordini-parziali-dettagli.component';

import { StatusSpedizioniComponent } from './components/status-spedizioni/status-spedizioni.component';
import { StatusSpedizioniDettaggliComponent } from './components/status-spedizioni/status-spedizioni-dettaggli/status-spedizioni-dettaggli.component';

import { BaseAgenteComponent } from './components/shared/base-agente/base-agente.component';
// AccessoFunzionariDettagliComponent removed in v2.3.0 — replaced by
// the in-page View modal of AccessoFunzionariListComponent.
import { AccessoFunzionariListComponent } from './components/accesso-funzionari/accesso-funzionari-list/accesso-funzionari-list.component';
import { AccessoFunzionariTrackingComponent } from './components/accesso-funzionari/accesso-funzionari-tracking/accesso-funzionari-tracking.component';
import { OrdiniAgenteDettaggliComponent } from './components/accesso-funzionari/ordini-agente-dettaggli/ordini-agente-dettaggli.component';
import { InfoAgenteComponent } from './components/accesso-funzionari/info-agente/info-agente.component';

import { GestioneAccountListComponent } from './components/gestione-account/gestione-account-list/gestione-account-list.component';
// GestioneAccountDettagli / -New / -Edit + the nested UploadComponent
// removed in v2.3.0 — replaced by the single-page restyle of
// GestioneAccountListComponent (modals + reactive form).

// MessaggiListComponent, MessaggiDettagliComponent, MessaggiNewComponent
// removed in v2.3.0 — replaced by ChatComponent (WhatsApp-style chat at
// /messaggi). See src/app/components/messaggi/chat/.
import { ChatComponent } from './components/messaggi/chat/chat.component';

import { GestioneProdottiListComponent } from './components/gestione-prodotti/gestione-prodotti-list/gestione-prodotti-list.component';
import { GestioneProdottiDettagliComponent } from './components/gestione-prodotti/gestione-prodotti-dettagli/gestione-prodotti-dettagli.component';
import { ProductDetailComponent } from './components/gestione-prodotti/product-detail/product-detail.component';
import { ProductPositionComponent } from './components/gestione-prodotti/product-position/product-position.component';

import { OrderscmDetComponent } from './components/shared/base-orderscm-list/orderscm-det/orderscm-det.component';
import { ProductDetailModalComponent } from './components/gestione-prodotti/product-detail-modal/product-detail-modal.component';
import { ProductDetailsListComponent } from './components/shared/order-details/product-details-list/product-details-list.component';
import { ConfirmCancelComponent } from './components/preparazione-ordini/confirm-cancel/confirm-cancel.component';
import { PopoverWarningComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/popover-warning/popover-warning.component';
import { ErroriComponent } from './components/storico-ordini/storico-ordini-dettagli/errori/errori.component';
import { ModifyErrorComponent } from './components/storico-ordini/storico-ordini-dettagli/errori/modify-error/modify-error.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ChangeOperatorComponent } from './components/preparazione-ordini/change-operator/change-operator.component';
import { ProductsQRCODESComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-products/products-qrcodes/products-qrcodes.component';
import { ListOfProductsForBoxesComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-boxes/list-of-products-for-boxes/list-of-products-for-boxes.component';
import { StoricoParzialiComponent } from './components/storico-parziali/storico-parziali.component';
import { AgenteListComponent } from './components/accesso-funzionari/agente-list/agente-list.component';
import { ByToClosingDateModule } from './pipes/by-to-closing-date/by-to-closing-date.module';
import { ByToSignDateModule } from './pipes/by-to-sign-date/by-to-sign-date.module';
import { ByBorderoModule } from './pipes/by-bordero/by-bordero.module';
import { FiestaComponent } from './components/shared/fiesta/fiesta.component';
import { DecimalPipe } from '@angular/common';
import { MonthModule } from './pipes/month.module';
import { ProductInfoComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/product-info/product-info.component';

import { ClientListComponent } from './components/accesso-funzionari/client-list/client-list.component';
import { SubproductQrcodeComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-products/subproduct-qrcode/subproduct-qrcode.component';
import { ProductDetailsStoricoComponent } from './components/storico-ordini/storico-ordini-dettagli/product-details-storico/product-details-storico.component';
import {
  TranslateModule,
  TranslateLoader,
  provideTranslateService,
} from '@ngx-translate/core';
import { IonicModule } from '@ionic/angular';
import { ManufacturerListComponent } from './components/manufacturer/manufacturer-list/manufacturer-list.component';
import { CheckOrderManufacturerComponent } from './components/manufacturer/check-order-manufacturer/check-order-manufacturer.component';
import { CheckOrderSelectorComponent } from './components/manufacturer/check-order-manufacturer/check-order-selector/check-order-selector.component';
import { CheckOrderArticlesComponent } from './components/manufacturer/check-order-manufacturer/check-order-articles/check-order-articles.component';
import { CheckOrderRangeComponent } from './components/manufacturer/check-order-manufacturer/check-order-range/check-order-range.component';
import { CheckOrderProductComponent } from './components/manufacturer/check-order-manufacturer/check-order-product/check-order-product.component';
import { CheckOrderUploadComponent } from './components/manufacturer/check-order-manufacturer/check-order-upload/check-order-upload.component';
import { CheckOrderCertificatesComponent } from './components/manufacturer/check-order-manufacturer/check-order-certificates/check-order-certificates.component';
import { CheckOrderLotsComponent } from './components/manufacturer/check-order-manufacturer/check-order-lots/check-order-lots.component';
import { GammaOrdersComponent } from './components/manufacturer/gamma-list/gamma-orders.component';
import { ProcessingComponent } from './components/manufacturer/manufacturer-list/processing/processing.component';
import { NonConformComponent } from './components/manufacturer/manufacturer-list/non-conform/non-conform.component';
import { HistoryComponent } from './components/manufacturer/manufacturer-list/history/history.component';
import { PartialComponent } from './components/manufacturer/manufacturer-list/partial/partial.component';
import { HistoryOrderDetailComponent } from './components/manufacturer/history-order-detail/history-order-detail.component';
import { SeeBatchComponent } from './components/manufacturer/popovers/see-batch/see-batch.component';
import { FornitoreListAccountsComponent } from './components/fornitore-list-accounts/fornitore-list-accounts.component';
import { FornitoriOrdiniListComponent } from './components/fornitori-ordini-list/fornitori-ordini-list.component';
import { FornitoriOrdiniDettagliComponent } from './components/fornitori-ordini-dettagli/fornitori-ordini-dettagli.component';
import { TracciabilitaFornitoriComponent } from './components/tracciabilita-fornitori/tracciabilita-fornitori.component';
import { TracciabilityFilterModalComponent } from './components/tracciabilita-fornitori/tracciability-filter-modal/tracciability-filter-modal.component';
import { FornitoriTrackingComponent } from './components/fornitori-tracking/fornitori-tracking.component';
import { EasterEggModalComponent } from './components/shared/easter-egg-modal/easter-egg-modal.component';
import { CreditsModalComponent } from './components/shared/credits-modal/credits-modal.component';
import { ReleaseTimelineModalComponent } from './components/shared/release-timeline-modal/release-timeline-modal.component';
import { LucideAngularModule, Home, Settings } from 'lucide-angular';


declare global {
  interface Window {
    WonderPush?: any;
  }
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SidebarComponent,
    DashboardComponent,
    OrdiniPMComponent,
    LoginComponent,
    FooterComponent,
    PageNotFoundComponent,
    BaseAgenteComponent,
    GammaOrdersComponent,
    ResetPasswordComponent,
    ProfileComponent,
    BaseOrdersListComponent,
    AssegnaOrdineListComponent,
    RightSidebarComponent,
    BaseOrderscmListComponent,
    StatusSpedizioniComponent,
    StoricoOrdiniComponent,
    StatisticheComponent,
    ClientListComponent,
    ControlloQualityComponent,
    AccessoFunzionariListComponent,
    GestioneAccountListComponent,
    AssegnaOrdineDettagliComponent,
    StoricoOrdiniDettagliComponent,
    ProductDetailComponent,
    GestioneProdottiListComponent,
    GestioneProdottiDettagliComponent,
    ProductPositionComponent,
    PreparazioneOrdiniComponent,
    AddBoxComponent,
    BoxDetailsComponent,
    PreparazioneOrdiniDetaggliComponent,
    CollectionComponent,
    ConfirmProductsComponent,
    ProductsQRCODESComponent,
    ConfirmBoxesComponent,
    ConfirmDocumentsComponent,
    ConfirmShipmentComponent,
    ConfirmCourrierComponent,

    WarehouseOrdersComponent,
    StatusSpedizioniDettaggliComponent,
    AccessoFunzionariTrackingComponent,
    OrdiniAgenteDettaggliComponent,
    OrdiniDaFirmareComponent,
    OrdiniDaFirmareDetaggliComponent,
    ConfirmCompleteComponent,
    OrderscmDetComponent,
    BorderoPageComponent,
    ProductDetailModalComponent,
    ProductDetailsListComponent,
    SettingsComponent,

    ConfirmCancelComponent,
    ChangeOperatorComponent,
    PopoverWarningComponent,
    AssegnaValoreComponent,
    ChatComponent,
    BorderòComponent,
    BorderòDettagliComponent,
    OrdiniParzialiListComponent,
    OrdiniParzialiDettagliComponent,
    ErroriComponent,
    InfoAgenteComponent,
    ModifyErrorComponent,
    ListOfProductsForBoxesComponent,
    StoricoParzialiComponent,
    AgenteListComponent,
    FiestaComponent,
    ProductInfoComponent,
    SubproductQrcodeComponent,
    ProductDetailsStoricoComponent,
    ManufacturerListComponent,
    CheckOrderManufacturerComponent,
    CheckOrderSelectorComponent,
    CheckOrderArticlesComponent,
    CheckOrderRangeComponent,
    CheckOrderProductComponent,
    CheckOrderUploadComponent,
    CheckOrderCertificatesComponent,
    CheckOrderLotsComponent,
    ProcessingComponent,
    NonConformComponent,
    HistoryComponent,
    PartialComponent,
    FornitoreListAccountsComponent,
    FornitoriOrdiniListComponent,
    FornitoriOrdiniDettagliComponent,
    TracciabilitaFornitoriComponent,
    TracciabilityFilterModalComponent,
    FornitoriTrackingComponent,
    HistoryOrderDetailComponent,
    CheckOrderArticlesComponent,
    SeeBatchComponent,
    EasterEggModalComponent,
    CreditsModalComponent,
    ReleaseTimelineModalComponent
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    IonicModule.forRoot({
      sanitizerEnabled: true,
      backButtonText: '',
    }),
    LucideAngularModule.pick({
      Home,
      Settings
    }),
    NgbModule,
    NgbPopoverModule,
    BrowserModule,
    AppRoutingModule,
    LabelModule,
    BrowserAnimationsModule,
    GridModule,
    MatProgressSpinnerModule,
    InputsModule,
    DropDownsModule,
    FormFieldModule,
    TextBoxModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogsModule,
    NotificationModule,
    DateInputsModule,
    ByPriorityCodeModule.forRoot(),
    ByOrderNumberModule.forRoot(),
    MonthModule.forRoot(),
    ByStatusCodeModule.forRoot(),
    ByNamePipeModule.forRoot(),
    SortByModule,
    ByCustomerNameModule,
    byDatePipeModule,
    ByToDateModule,
    ByToClosingDateModule.forRoot(),
    ByToSignDateModule.forRoot(),
    ByBorderoModule.forRoot(),
    ByMsCarrierModule,
    FontAwesomeModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'it-IT',
    },
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'it',
      lang: 'it',
    }),
    DecimalPipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
